import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  ShoppingBag, Plus, Pencil, Trash2, Loader2, Search,
  ExternalLink, Image, DollarSign
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

const CATEGORIES = [
  "Clothing", "Accessories", "Home", "Books", "Memorabilia", 
  "Training", "Electronics", "Gift Cards", "Other"
];

const ShopAdmin = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState({ category: "all" });
  const [editingProduct, setEditingProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const emptyProduct = {
    name: "",
    description: "",
    price: 0,
    category: "Clothing",
    image_url: "",
    affiliate_link: "",
    is_available: true
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/shop/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products || response.data || []);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const saveProduct = async () => {
    try {
      if (editingProduct.id) {
        await axios.put(`${API}/admin/shop/products/${editingProduct.id}`, editingProduct, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/shop/products`, editingProduct, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Product created");
      }
      setDialogOpen(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const deleteProduct = async (id) => {
    if (!confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/shop/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Product deleted");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const filteredProducts = products.filter(p => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!p.name?.toLowerCase().includes(query) && !p.description?.toLowerCase().includes(query)) return false;
    }
    if (filter.category !== "all" && p.category !== filter.category) return false;
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="shop-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <ShoppingBag className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          SHOP ADMIN
        </h1>
        <Button
          onClick={() => { setEditingProduct({...emptyProduct}); setDialogOpen(true); }}
          className="bg-[#7C2C3B] hover:bg-[#63232F]"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-[#0F172A]">{products.length}</div>
          <div className="text-sm text-slate-500">Total Products</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-green-600">{products.filter(p => p.is_available).length}</div>
          <div className="text-sm text-slate-500">Available</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-blue-600">{products.filter(p => p.affiliate_link).length}</div>
          <div className="text-sm text-slate-500">With Affiliate Links</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-purple-600">{new Set(products.map(p => p.category)).size}</div>
          <div className="text-sm text-slate-500">Categories</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="product-search"
          />
        </div>
        <Select value={filter.category} onValueChange={(v) => setFilter({...filter, category: v})}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[450px]">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-100 z-10">
              <TableRow>
                <TableHead className="w-16">Image</TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="w-28">Category</TableHead>
                <TableHead className="w-24">Price</TableHead>
                <TableHead className="w-24">Link</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.map(product => (
                <TableRow key={product.id} className={!product.is_available ? "opacity-50" : ""}>
                  <TableCell>
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-10 h-10 object-cover rounded"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                        <Image className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{product.name}</div>
                    <div className="text-xs text-slate-500 truncate max-w-xs">{product.description?.substring(0, 60)}...</div>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs px-2 py-1 rounded bg-slate-100">
                      {product.category}
                    </span>
                  </TableCell>
                  <TableCell className="font-bold text-green-600">
                    {typeof product.price === 'string' && product.price.startsWith('£') 
                      ? product.price 
                      : `£${Number(product.price || 0).toFixed(2)}`}
                  </TableCell>
                  <TableCell>
                    {product.affiliate_link ? (
                      <a 
                        href={product.affiliate_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline text-xs"
                      >
                        Amazon <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-slate-400 text-xs">No link</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded ${product.is_available ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {product.is_available ? "Available" : "Unavailable"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0"
                        onClick={() => { setEditingProduct({...product}); setDialogOpen(true); }}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                        onClick={() => deleteProduct(product.id)}
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredProducts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    No products found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-2 text-center">
        Showing {filteredProducts.length} of {products.length} products • Affiliate code: beckenham-21
      </p>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingProduct?.id ? "EDIT PRODUCT" : "ADD NEW PRODUCT"}
            </DialogTitle>
          </DialogHeader>
          
          {editingProduct && (
            <div className="grid gap-4 py-4">
              {editingProduct.image_url && (
                <div className="flex justify-center">
                  <img 
                    src={editingProduct.image_url} 
                    alt="Preview" 
                    className="w-24 h-24 object-cover rounded-lg border"
                  />
                </div>
              )}
              
              <div>
                <Label>Product Name *</Label>
                <Input 
                  value={editingProduct.name} 
                  onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={editingProduct.description || ""} 
                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={editingProduct.category} onValueChange={(v) => setEditingProduct({...editingProduct, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4" /> Price (£)
                  </Label>
                  <Input 
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingProduct.price || 0} 
                    onChange={(e) => setEditingProduct({...editingProduct, price: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Image URL</Label>
                <Input 
                  value={editingProduct.image_url || ""} 
                  onChange={(e) => setEditingProduct({...editingProduct, image_url: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Amazon Affiliate Link</Label>
                <Input 
                  value={editingProduct.affiliate_link || ""} 
                  onChange={(e) => setEditingProduct({...editingProduct, affiliate_link: e.target.value})}
                  placeholder="https://amazon.co.uk/..."
                />
                <p className="text-xs text-slate-500 mt-1">Include affiliate tag: beckenham-21</p>
              </div>
              
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  checked={editingProduct.is_available}
                  onChange={(e) => setEditingProduct({...editingProduct, is_available: e.target.checked})}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <Label className="mb-0">Product is available</Label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveProduct} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-product-btn">
              {editingProduct?.id ? "Update" : "Create"} Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShopAdmin;
